import type { IForegroundContentBbox } from 'types/store';
import { FULL_CONTENT_BBOX } from 'types/store';

/**
 * Scan a cutout image for non-transparent content and return the bounding box as 0..1 fractions
 * of the image's natural dimensions. Pixels matching the top-left pixel are treated as background.
 *
 * Mirrors the pixel-comparison strategy used by computeImageOffsets — the cutout PNG returned by
 * the remove-bg pipeline keeps the source's original dimensions with the subject surrounded by
 * transparent (or uniform top-left) padding, so we have to detect bounds on the client.
 */
const computeForegroundBbox = async (imgElement: HTMLImageElement): Promise<IForegroundContentBbox> => {
  return new Promise<IForegroundContentBbox>(resolve => {
    const canvas = document.createElement('canvas');
    canvas.width = imgElement.naturalWidth;
    canvas.height = imgElement.naturalHeight;
    const ctx = canvas.getContext('2d');

    if (!ctx || !canvas.width || !canvas.height) return resolve(FULL_CONTENT_BBOX);

    ctx.drawImage(imgElement, 0, 0);

    let imageData: ImageData;
    try {
      imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    } catch {
      // Tainted canvas (cross-origin image without CORS) — fall back to the full rect.
      return resolve(FULL_CONTENT_BBOX);
    }

    const { data, width, height } = imageData;
    const topLeftR = data[0];
    const topLeftG = data[1];
    const topLeftB = data[2];
    const topLeftA = data[3];

    let minX = width;
    let minY = height;
    let maxX = 0;
    let maxY = 0;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const idx = (y * width + x) * 4;
        if (data[idx] !== topLeftR || data[idx + 1] !== topLeftG || data[idx + 2] !== topLeftB || data[idx + 3] !== topLeftA) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }

    if (minX > maxX || minY > maxY) {
      // No content detected — treat the full rect as content so the transformer is still usable.
      return resolve(FULL_CONTENT_BBOX);
    }

    resolve({
      minX: minX / width,
      minY: minY / height,
      maxX: (maxX + 1) / width,
      maxY: (maxY + 1) / height,
    });
  });
};

export default computeForegroundBbox;
